import { cn } from '@invent/wl-ui-kit-next';
import { Card, Switch } from '@invent/wl-ui-kit-next/ui';
import { ChevronRight } from 'lucide-react';
import * as React from 'react';

import { readViewerChoice, writeViewerChoice } from './viewer-store';
import { useViewer } from './zone';

/**
 * A record's section that folds, and the grid of labelled fields inside it — the advisor record
 * page's Lightning-like sections (the advisor record page's roadmap, K3; its ruling U7).
 *
 * **A `<details>` with the heading inside its `<summary>`**, not a heading holding a disclosure
 * button. Checked in Chromium before it was chosen (the F1 kit plan's journal, 2026-10-03): the
 * heading inside the summary stays a heading — Playwright's `getByRole('heading', { level })` finds
 * it and Chromium's tree keeps it under the disclosure — and the summary is operable from the
 * keyboard. What `<details>` gives that the button pattern would have to rebuild is find-in-page:
 * Chromium opens a folded section holding a match, where a `hidden` body stays hidden.
 *
 * **Open and "Show empty fields" are remembered per viewer and per kind of section**
 * (`useSectionMemory`, `veritant.sections.<page>.<viewer>`), never per record: a section opened on
 * one advisor is open on the next, as Lightning does it. **Only a choice is remembered** — the
 * summary's own press and the switch. A section opened by the section nav, or by find-in-page, is
 * open for the visit and forgotten. The `toggle` event cannot tell those apart (and Chromium
 * coalesces toggles queued together into one), so the summary's press does not reach `<details>`'s
 * own activation at all: it is cancelled and the open state flipped and stored here, and `toggle`
 * only follows what something else did to the element.
 *
 * **Empty fields are hidden until asked for**: each `FieldGridItem` says whether it is `filled`,
 * the section says "N of M filled" in its summary, and the switch in its body — drawn only while
 * something is empty — shows the rest. A section with nothing filled says so beside the switch.
 * **Inside `EveryEmptyScope`** (SR-427) one switch over several sections stands over each one's:
 * while it is on, every section shows its empties and draws no switch of its own.
 * **A field emptied on the page stays drawn** (`kept`, F2's Q2 ruled (a)) until the switch is next
 * used: a field that left the grid as its value was cleared would take the focus on its pencil with
 * it, and the person who cleared it would lose sight of what they just did.
 *
 * **`lazy` mounts the body the first time the section is open** — by its own press, the section
 * nav, find-in-page or a remembered choice — and keeps it mounted after a fold. It is for a body
 * that reads something of its own (the hub's Records, F3's R4), which would otherwise read on
 * load for every section, opened or not. Its cost: find-in-page cannot reach what was never
 * mounted, so a lazy section never opened is not searched. Without it the body is always in the
 * DOM, `<details>`' own behaviour.
 */

/** A section's remembered choice; a member absent is the caller's default. */
export interface ISectionChoice {
  open?: boolean;
  empties?: boolean;
}

/**
 * A page's section memory for the viewer signed in — `page` names the kind of page
 * (`advisor-record`), each section its kind (`research.FA.information`). A write reads the stored
 * object afresh and changes one section's entry, so two sections writing in one visit do not
 * overwrite each other with the copy each read when it mounted.
 */
export function useSectionMemory(page: string): {
  read: (sectionKey: string) => ISectionChoice;
  write: (sectionKey: string, choice: ISectionChoice) => void;
} {
  const viewer = useViewer();
  const key = `veritant.sections.${page}.${viewer}`;
  return React.useMemo(
    () => ({
      read: (sectionKey) => choiceOf(readViewerChoice(key)[sectionKey]),
      write: (sectionKey, choice) => {
        const stored = readViewerChoice(key);
        const merged = { ...choiceOf(stored[sectionKey]), ...choice };
        writeViewerChoice(key, { ...stored, [sectionKey]: merged });
      }
    }),
    [key]
  );
}

function choiceOf(value: unknown): ISectionChoice {
  if (!value || typeof value !== 'object') return {};
  const { open, empties } = value as Record<string, unknown>;
  return {
    ...(typeof open === 'boolean' && { open }),
    ...(typeof empties === 'boolean' && { empties })
  };
}

/** Whether the grids inside a section show their empty fields — outside any section, every one —
 *  and how many times its switch has been used, which lets a `kept` field go. */
const ShowEmpties = React.createContext<{ show: boolean; used: number }>({ show: true, used: 0 });

/**
 * One switch over every section inside it (SR-427): Veritant research's part shows each group's
 * empty fields at once, as a Salesforce page layout shows a record's blank fields. While it is on,
 * each section inside shows its empties and draws no switch of its own; while it is off, each
 * section's own remembered switch stands. Its uses count as the sections' own, so a `kept` field
 * goes when either switch is next used.
 */
const EveryEmpty = React.createContext<{ show: boolean; used: number }>({ show: false, used: 0 });

/** A switch over several sections' empty fields, remembered like a section's own under
 *  `sectionKey` (`useSectionMemory`): draw `switch` where it belongs and wrap the sections in
 *  `Scope`. */
export function useEveryEmpty(
  page: string,
  sectionKey: string
): { show: boolean; used: number; set: (on: boolean) => void } {
  const memory = useSectionMemory(page);
  const [show, setShow] = React.useState(() => memory.read(sectionKey).empties ?? false);
  const [used, setUsed] = React.useState(0);
  React.useEffect(() => setShow(memory.read(sectionKey).empties ?? false), [memory, sectionKey]);
  return {
    show,
    used,
    set: (on) => {
      setShow(on);
      setUsed((count) => count + 1);
      memory.write(sectionKey, { empties: on });
    }
  };
}

export function EveryEmptyScope({
  show,
  used,
  children
}: {
  show: boolean;
  used: number;
  children: React.ReactNode;
}) {
  const value = React.useMemo(() => ({ show, used }), [show, used]);
  return <EveryEmpty.Provider value={value}>{children}</EveryEmpty.Provider>;
}

/** The switch's look, for a section's own and for `useEveryEmpty`'s. */
export function EmptiesSwitch({
  checked,
  onCheckedChange,
  label,
  children = 'Show empty fields'
}: {
  checked: boolean;
  onCheckedChange: (on: boolean) => void;
  /** The accessible name — what the switch shows, said whole. */
  label: string;
  children?: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-2 text-muted-foreground">
      <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={label} />
      <span>{children}</span>
    </label>
  );
}

export function RecordSection({
  page,
  sectionKey,
  title,
  level,
  defaultOpen,
  filled,
  total,
  anchor,
  lazy,
  flush,
  empty = false,
  children
}: {
  /** The kind of page the memory is kept for. */
  page: string;
  /** The kind of section — stable across records, and the `data-section` hook. */
  sectionKey: string;
  title: string;
  /** 2 for a section of the page, 3 for one inside another, 4 for one inside a section's part
   *  (Veritant research's groups, under a survey's heading). */
  level: 2 | 3 | 4;
  /** Open on a viewer's first visit, and wherever their store holds nothing. */
  defaultOpen: boolean;
  /** How many of the section's fields hold a value, and how many it has. */
  filled: number;
  total: number;
  /** Makes the section a place the section nav scrolls to, opening it on the way. */
  anchor?: string;
  /** Mount the body only from the section's first opening on. */
  lazy?: boolean;
  /** The body brings its own inset — a card's `CardContent`, as a hub section's does (F11's Q2) —
   *  so the fold adds none of its own. */
  flush?: boolean;
  /** Nothing to show — a related list with no rows (SR-407): drawn as its title and "(0)", folded
   *  whatever the viewer's memory says, Salesforce's empty related list. A press or the section nav
   *  still opens it — on its own state, never stored — to its sentence; the body stays mounted, so
   *  a read inside it that finds rows later is drawn as the section remembers it. */
  empty?: boolean;
  children: React.ReactNode;
}) {
  const memory = useSectionMemory(page);
  const every = React.useContext(EveryEmpty);
  // An empty fold's own open state: shut at first, never written to the memory.
  const [peek, setPeek] = React.useState(false);
  const [open, setOpen] = React.useState(() => memory.read(sectionKey).open ?? defaultOpen);
  const [empties, setEmpties] = React.useState(() => memory.read(sectionKey).empties ?? false);
  const [used, setUsed] = React.useState(0);
  // Another viewer's store (or another kind of section) is another memory. `defaultOpen` is left
  // out on purpose: a default that moves with the data must not shut a section the viewer opened.
  React.useEffect(() => {
    const choice = memory.read(sectionKey);
    setOpen(choice.open ?? defaultOpen);
    setEmpties(choice.empties ?? false);
  }, [memory, sectionKey]);
  // Every way of opening ends in `open` — the press, `toggle` after the nav or find-in-page, the
  // memory's effect — so the state is adjusted while rendering, React's pattern for a value derived
  // from a change, rather than in an effect that would draw one frame of an open, empty fold.
  const [seen, setSeen] = React.useState(open);
  if (open && !seen) setSeen(true);

  const headingId = React.useId();
  const Heading = (['h2', 'h3', 'h4'] as const)[level - 2];

  const press = (event: React.MouseEvent) => {
    event.preventDefault();
    if (empty) {
      setPeek((now) => !now);
      return;
    }
    const next = !open;
    setOpen(next);
    memory.write(sectionKey, { open: next });
  };

  const showEmpties = (on: boolean) => {
    setEmpties(on);
    setUsed((count) => count + 1);
    memory.write(sectionKey, { empties: on });
  };

  const content = !lazy || seen ? children : null;

  const body = (
    <details
      id={anchor}
      open={empty ? peek : open}
      onToggle={(event) => (empty ? setPeek : setOpen)(event.currentTarget.open)}
      className={cn(anchor && 'scroll-mt-4 lg:scroll-mt-[calc(var(--sticky-top,3rem)+0.5rem+var(--section-bar-h,2.5rem))]')}
    >
      <summary
        onClick={press}
        className={cn(
          'flex cursor-pointer list-none items-baseline gap-2 [&::-webkit-details-marker]:hidden',
          level === 2 ? 'px-5 py-4' : 'py-3'
        )}
      >
        <ChevronRight
          // Turned by its own `<details>` alone: a `group-open` would read any open ancestor, so a
          // section folded inside another's open body drew its chevron open.
          className={cn(
            'size-4 shrink-0 self-center text-muted-foreground transition-transform [details[open]>summary>&]:rotate-90',
            empty && 'opacity-30'
          )}
          aria-hidden
        />
        <Heading
          id={headingId}
          className={cn(
            'inline font-medium text-[var(--color-royal-blue)]',
            level === 2 ? 'text-[0.9375rem]' : 'text-sm'
          )}
        >
          {title}
        </Heading>
        {empty && <span className="text-sm text-muted-foreground">(0)</span>}
        {total > 0 && (
          <span className="text-xs text-muted-foreground">
            {filled} of {total} filled
          </span>
        )}
      </summary>
      <div className={cn('grid', !flush && ['gap-4', level === 2 ? 'px-5 pb-5' : 'pb-4'])}>
        {filled < total && !every.show && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <EmptiesSwitch
              checked={empties}
              onCheckedChange={showEmpties}
              label={`Show empty fields in ${title}`}
            />
            {filled === 0 && !empties && (
              <span className="text-muted-foreground">No field here has a value</span>
            )}
          </div>
        )}
        {/* A fold that counts no fields — a hub section, Records' objects — has no switch, and
            says nothing about the grids inside it: they keep the switch nearest them, or show
            every field. */}
        {total > 0 ? (
          <ShowEmpties.Provider value={{ show: empties || every.show, used: used + every.used }}>
            {content}
          </ShowEmpties.Provider>
        ) : (
          content
        )}
      </div>
    </details>
  );

  const hooks = {
    role: 'region',
    'aria-labelledby': headingId,
    'data-section': sectionKey,
    'data-open': (empty ? peek : open) || undefined,
    'data-empty': empty || undefined
  } as const;

  return level === 2 ? (
    <Card {...hooks}>{body}</Card>
  ) : (
    <section {...hooks} className="border-t border-border">
      {body}
    </section>
  );
}

/**
 * Labelled fields, label over value — two columns from `sm`, one on a phone. **`framed`** counts by
 * the nearest `@container` instead, two from 34rem: a grid drawn in a pane beside something else
 * (Lists' reading pane) is narrow on a wide screen, and two columns there left each control half
 * of it.
 */
export function FieldGrid({ children, framed = false }: { children: React.ReactNode; framed?: boolean }) {
  return (
    <dl className={cn('grid gap-x-6 gap-y-4', framed ? '@min-[34rem]:grid-cols-2' : 'sm:grid-cols-2')}>
      {children}
    </dl>
  );
}

/**
 * One labelled field. Hidden while it is empty and its section shows no empty fields; `wide` spans
 * every column the grid draws — both, or the one — for a value that is a paragraph rather than a
 * word, so it follows whichever rule counts them (`framed` or not) with no breakpoint of its own.
 * `kept` holds an empty field in the grid anyway — one emptied on this visit — until its section's
 * switch is next used, or it holds a value again (and is kept afresh if emptied again).
 */
export function FieldGridItem({
  label,
  filled,
  wide,
  kept,
  children
}: {
  label: string;
  filled: boolean;
  wide?: boolean;
  kept?: boolean;
  children: React.ReactNode;
}) {
  const { show, used } = React.useContext(ShowEmpties);
  // The switch's count when the field was kept: a later use of the switch lets it go. Written while
  // rendering, as the same answer for the same props, so a render repeated is harmless.
  const keptAt = React.useRef<Nullable<number>>(null);
  if (!kept || filled) keptAt.current = null;
  else keptAt.current ??= used;
  if (!filled && !show && keptAt.current !== used) return null;
  return (
    <div
      data-filled={filled || undefined}
      className={cn('grid content-start gap-0.5', wide && 'col-span-full')}
    >
      <dt className="text-xs tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className="wrap-anywhere">{children}</dd>
    </div>
  );
}
